import { useEffect, useMemo, useState } from "react";
import { X, Tag, Handshake, Search } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import { buildTurnInSeed } from "../../utils/buildTurnInSeed";

// Deal stages that are NOT a sale yet — these are valid source requests
// to turn into a sale. Sale-stage deals (Under Contract, Pending, Closed)
// are filtered out.
const PRE_SALE_STAGES = [
  "Prospect",
  "On Hold",
  "CMAs",
  "Listing Appointment",
  "Pre-Market",
  "Coming Soon",
  "Active",
];

// Roles that see everyone's requests (others see only their own).
const ALL_REQUEST_ROLES = ["Admin", "Supervisor", "Accountant"];

export default function SaleTurnInSourcePicker({ onSelect, onClose }) {
  const [{ user, deals }] = useStateValue();
  const [side, setSide] = useState(null); // null | "listing" | "buyer"
  const [search, setSearch] = useState("");
  const [pickedId, setPickedId] = useState(null);

  // Filter pool: matching dealtype + pre-sale stage + role-scoped
  const pool = useMemo(() => {
    if (!side) return [];
    const targetType = side === "listing" ? "Seller" : "Buyer";
    const seesAll = ALL_REQUEST_ROLES.includes(user.role);
    return (deals || []).filter(
      (d) =>
        d.requestType === "fileRequest" &&
        d.dealtype === targetType &&
        PRE_SALE_STAGES.includes(d.dealStage) &&
        (seesAll || d.agentData === user.id)
    );
  }, [side, deals, user]);

  // Auto-skip phase 2 if the pool is empty after side is chosen.
  useEffect(() => {
    if (side !== null && pool.length === 0) {
      onSelect(null);
    }
  }, [side, pool.length, onSelect]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(
      (d) =>
        (d.propertyaddress || "").toLowerCase().includes(q) ||
        (d.clientLastName || "").toLowerCase().includes(q)
    );
  }, [pool, search]);

  const handleContinue = () => {
    const picked = pool.find((d) => d.createdat === pickedId);
    if (!picked) return;
    onSelect(buildTurnInSeed(picked));
  };

  const handleScratch = () => onSelect(null);

  // Side picker phase
  if (side === null) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="text-base font-semibold text-gray-900">Sale Turn In</div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
              <X size={18} />
            </button>
          </div>
          <div className="p-6">
            <div className="text-sm text-gray-700 font-medium mb-4 text-center">
              Where Is This Sale Coming From?
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSide("listing")}
                className="border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg p-4 flex flex-col items-center gap-2 transition"
              >
                <Tag size={22} className="text-blue-600" />
                <div className="text-sm font-semibold text-gray-900">Listing Side</div>
                <div className="text-xs text-gray-500 text-center">We Listed It</div>
              </button>
              <button
                type="button"
                onClick={() => setSide("buyer")}
                className="border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg p-4 flex flex-col items-center gap-2 transition"
              >
                <Handshake size={22} className="text-blue-600" />
                <div className="text-sm font-semibold text-gray-900">Buyer Side</div>
                <div className="text-xs text-gray-500 text-center">We Represent the Buyer</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Phase 2: request picker (also covers the brief render before auto-skip)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="text-base font-semibold text-gray-900">
            Pick {side === "listing" ? "a Listing" : "a Buyer Request"}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by address or client…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-3 space-y-2">
          {filtered.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-6">No matching requests.</div>
          ) : (
            filtered.map((d) => (
              <button
                key={d.createdat}
                type="button"
                onClick={() => setPickedId(d.createdat)}
                className={`w-full text-left rounded-lg p-3 border-2 transition ${
                  pickedId === d.createdat
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <div className="font-semibold text-gray-900 text-sm">
                  {d.propertyaddress || "(no address)"}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {d.clientLastName || "—"} • {d.dealStage || "—"}
                </div>
              </button>
            ))
          )}
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleScratch}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Start From Scratch
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!pickedId}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}
